import React, { useCallback, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, LogOut, Building2, User as UserIcon, UsersRound } from 'lucide-react';
import TenantSwitcher from './TenantSwitcher';
import NotificationBell from './NotificationBell';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TopBar = ({ onMenuClick }) => {
  const { user, logout, isReportingManager, managerTeamSize, activeView, setActiveView } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isReportingManager) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/manager/pending-count`, { withCredentials: true });
        if (alive) setPendingCount(res.data?.total || 0);
      } catch (e) { /* non-fatal */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isReportingManager]);

  const fetchContextLabels = useCallback(async () => {
    if (!user || user.role === 'super_admin') return;
    try {
      const tenantsRes = await axios.get(`${API_URL}/tenants`, { withCredentials: true }).catch(() => null);
      if (tenantsRes) {
        const t = tenantsRes.data?.find((x) => x.id === user.tenant_id);
        if (t) setTenantName(t.name || '');
      }
      if (user.company_id) {
        const companiesRes = await axios.get(`${API_URL}/companies`, { withCredentials: true }).catch(() => null);
        if (companiesRes) {
          const c = companiesRes.data?.find((x) => x.id === user.company_id);
          if (c) setCompanyName(c.name || '');
        }
      }
    } catch (e) { /* non-fatal */ }
  }, [user]);

  useEffect(() => {
    fetchContextLabels();
  }, [fetchContextLabels]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="fixed top-0 left-0 md:left-20 lg:left-[280px] right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4 md:px-6 lg:px-8">
      <div className="flex items-center flex-1 max-w-2xl gap-3">
        <button
          onClick={onMenuClick}
          className="md:hidden p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
          title="Open Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search users, companies, employees..."
            data-testid="global-search-input"
            className="w-full h-10 pl-10 pr-4 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0052FF] focus:border-[#0052FF]"
          />
        </div>
      </div>

      <div className="flex items-center space-x-4">
        {/* Stage 73.20dg — View Switcher: shows only when the logged-in user
            is mapped as Reporting Manager for ≥1 active employee. */}
        {isReportingManager && (
          <div
            data-testid="view-switcher"
            className="hidden md:inline-flex items-center bg-slate-100 rounded-full p-0.5"
          >
            <button
              data-testid="view-switch-employee"
              onClick={() => { setActiveView('employee'); navigate('/dashboard'); }}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition ${
                activeView === 'employee'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              title="Your own requests + payslips + profile"
            >
              <UserIcon className="w-3.5 h-3.5" />
              My Employee View
            </button>
            <button
              data-testid="view-switch-manager"
              onClick={() => { setActiveView('manager'); navigate('/manager/dashboard'); }}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition ${
                activeView === 'manager'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              title={`Approve requests of your ${managerTeamSize} team member${managerTeamSize === 1 ? '' : 's'}`}
            >
              <UsersRound className="w-3.5 h-3.5" />
              Manager View
              <span className="ml-0.5 text-[10px] bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5">
                {managerTeamSize}
              </span>
              {pendingCount > 0 && (
                <span data-testid="manager-pending-badge"
                  title={`${pendingCount} request${pendingCount === 1 ? '' : 's'} awaiting your approval`}
                  className="ml-0.5 text-[10px] bg-red-600 text-white rounded-full px-1.5 py-0.5 font-bold">
                  {pendingCount}
                </span>
              )}
            </button>
          </div>
        )}
        {user?.role !== 'super_admin' && (tenantName || companyName) && (
          <div data-testid="tenant-company-badge"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-50 border border-indigo-200 text-xs">
            <Building2 className="w-3.5 h-3.5 text-indigo-600" />
            {tenantName && <span className="font-medium text-indigo-900">{tenantName}</span>}
            {tenantName && companyName && <span className="text-indigo-400">·</span>}
            {companyName && <span className="text-indigo-700">{companyName}</span>}
          </div>
        )}
        <TenantSwitcher />
        <NotificationBell />

        <div className="relative">
          {(() => {
            const empCode = user?.employee_code || user?.emp_code || user?.employeeCode || user?.code || '';
            return (
              <>
                <button
                  data-testid="profile-menu-button"
                  className="flex items-center space-x-3 hover:bg-slate-100 rounded-md px-3 py-2 transition-colors"
                  onClick={() => setShowProfile(!showProfile)}
                >
                  <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white font-medium text-sm">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                      <span>{user?.name}</span>
                      {empCode && (
                        <span className="text-slate-500 font-normal text-xs" data-testid="header-employee-code">
                          ({empCode})
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
                </button>

                {showProfile && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 mb-1">
                      <p className="text-xs font-semibold text-slate-900">{user?.name}</p>
                      {empCode && (
                        <p className="text-[11px] font-medium text-[#0052FF] mt-0.5" data-testid="dropdown-employee-code">
                          Employee Code: {empCode}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                    </div>
                    <button
                      data-testid="profile-link"
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={() => { navigate('/profile'); setShowProfile(false); }}
                    >
                      My Profile
                    </button>
                    <button
                      data-testid="notif-prefs-link"
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={() => { navigate('/notifications/preferences'); setShowProfile(false); }}
                    >
                      Notification Preferences
                    </button>
                    <hr className="my-2 border-slate-200" />
                    <button
                      data-testid="logout-button"
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default TopBar;
